import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { FormHandles } from '@unform/core';
import { FiArrowLeft } from 'react-icons/fi';

import { show } from 'store/modules/dialog/actions';

import api from 'services/api';

import { toast } from 'react-toastify';
import Card from 'components/Card';
import Button from 'components/Button';
import Input from 'components/Input';
import Radio from 'components/Radio';
import Checkbox from 'components/Checkbox';
import Spinner from 'components/Spinner';

import { Role, Roles, Setores } from 'utils/variables';

import { Container, Content, Form, FormContent, FormActions } from './styles';
import { Emissora } from 'pages/@types';
import getValidationErrors from 'services/getValidationErros';
import AppError from 'utils/AppError';

// configuração do validator
const schema = Yup.object().shape({
  nome: Yup.string().required('Campo Obrigatório'),
  email: Yup.string()
    .email('Insira um e-mail válido')
    .required('Campo Obrigatório'),
  role: Yup.string()
    .nullable()
    .required('Campo Obrigatório'),
  emissora: Yup.string().required('Escolha pelo menos uma emissora'),
});

export default function Usuarios() {
  // react-router-dom
  const navigate = useNavigate();
  // variaveis
  const [emissoras, setEmissoras] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSetorField, setShowSetorField] = useState(false);
  const [filteredSetores, setFilteredSetores] = useState(Setores);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const { usuarioId } = useParams<{ usuarioId: string }>();
  const [initialData, setInitialData] = useState({
    id: 0,
    nome: '',
    email: '',
    role: '',
    setor: '',
    emissora: [],
    // avaliadorprod: 'nao',
    // comunicador: 'nao',
    status: '',
  });

  // redux
  const dispatch = useDispatch();

  // referencia
  const formRef = useRef<FormHandles>(null);
  // metodos

  /**
   * USE CALLBACK
   */

  const updateSetor = useCallback(
    value => {
      const set = Setores.find(set => set.role_reference === value.id);
      if ([Role.PROMO, Role.ADMIN].includes(value.id)) {
        setFilteredSetores(
          value.id === Role.PROMO
            ? Setores.filter(set => set.role_reference === Role.PROMO)
            : Setores,
        );
        setShowSetorField(true);
      } else {
        setShowSetorField(false);
        setFilteredSetores(Setores);
      }
      if (set && formRef.current) {
        formRef.current.setFieldValue('setor', set.id);
      }
    },

    [],
  );

  /**
   * USE EFFECT
   */
  useEffect(() => {
    async function loadEmissoras() {
      await api
        .get('/emissoras')
        .then(response => {
          if (response && response.data)
            setEmissoras(
              response.data.map((emi: Emissora) => {
                return { id: emi.id, label: emi.nome };
              }),
            );
          else toast.error('Falha de comunicação com o sistema');
        })
        .catch(error => {
          if (error instanceof Error) {
            toast.error(error.message);
          } else console.log(error);
        });
    }
    loadEmissoras();
  }, []);

  useEffect(() => {
    async function loadUsuario() {
      setLoading(true);
      try {
        const response = await api.get(`/usuarios/${usuarioId}`);
        if (response?.data) {
          setShowSetorField(
            !![Role.ADMIN, Role.PROMO].includes(response.data.role),
          );
          setInitialData({
            ...response.data,
            emissora: response.data.emissora.map((emi: Emissora) => emi.id),
          });
          setLoading(false);
        } else toast.error('Falha de comunicação com o sistema');
      } catch (error) {
        if (error instanceof Error) {
          toast.error(error.message);
        } else {
          const appError = error as AppError;
          toast.error(appError.message);
        }
      }
    }
    // Verifica se é edição
    if (usuarioId) loadUsuario();
  }, [usuarioId]);

  const handleSuccessMessage = useCallback(
    data => {
      const option = {
        type: 'success',
        confirmButtonText: '',
        timer: 3000,
        cancelButtonText: 'NÃO',
        showCancelButton: false,
        showConfirmButton: false,
        showCloseButton: true,
        ...data,
      };

      dispatch(show(option));
    },
    [dispatch],
  );

  const handleSubmit = useCallback(
    async data => {
      try {
        setLoadingSubmit(true);
        if (formRef.current) formRef.current.setErrors({});
        await schema.validate({ ...data }, { abortEarly: false });
        let response;
        if (initialData.id)
          response = await api.put(`/usuarios/${usuarioId}`, data);
        else response = await api.post('/usuarios', data);

        handleSuccessMessage({
          contentHTML: initialData.id
            ? `<h3>Usuário atualizado!</h3> <p>Você será redirecionado para a lista de usuarios em 3 segundos</p>`
            : `<h3>Usuário criado!</h3><br/> <p>Um email com instruções de acesso será enviado para <b>${response.data.email}</b></p>`,
          redirectTo: '/usuarios',
        });
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          if (formRef.current) formRef.current.setErrors(errors);
          toast.error(err.message);
        } else if (err instanceof Error) {
          toast.error(err.message);
        } else {
          const appError = err as AppError;
          toast.error(appError.message);
        }
      } finally {
        setLoadingSubmit(false);
      }
    },
    [handleSuccessMessage, initialData.id, usuarioId],
  );

  // async function handleEdit(data) {
  //   try {
  //     setLoadingSubmit(true);
  //     formRef.current.setErrors({});
  //     await schema.validate({ ...data }, { abortEarly: false });

  //     await api.put(`/usuarios/${usuarioId}`, data);

  //     handleSuccessMessage({
  //       contentHTML: `<h3>Usuário atualizado!</h3> <p>Você será redirecionado para a lista de usuarios em 3 segundos</p>`,
  //       redirectTo: '/usuarios',
  //     });
  //     setLoadingSubmit(false);
  //   } catch (err) {
  //     const validationErrors = {};
  //     if (err instanceof Yup.ValidationError) {
  //       err.inner.forEach(error => {
  //         validationErrors[error.path] = error.message;
  //       });
  //       formRef.current.setErrors(validationErrors);
  //     }

  //     toast.error(err.message);

  //     setLoadingSubmit(false);
  //   }
  // }

  return (
    <Container>
      <header>
        <Button
          color="primary"
          onClick={e => {
            e.preventDefault();
            navigate(-1);
          }}
        >
          VOLTAR
          <FiArrowLeft />
        </Button>
      </header>
      <Card>
        <header>
          <h4>ADICIONAR USUÁRIO</h4>
        </header>
        <Content>
          {!loading && (
            <Form
              ref={formRef}
              initialData={initialData}
              onSubmit={handleSubmit}
              autoComplete="off"
            >
              <FormContent>
                <section>
                  <h5>Dados gerais: </h5>
                </section>
                <Input
                  name="nome"
                  placeholder="Nome completo *"
                  disabled={loadingSubmit || initialData.status === 'inativo'}
                />
                <Input
                  name="email"
                  placeholder="E-email *"
                  disabled={loadingSubmit || initialData.status === 'inativo'}
                />
                <Input
                  max={45}
                  name="telefone"
                  placeholder="Telefone"
                  disabled={loadingSubmit || initialData.status === 'inativo'}
                />
                <section>
                  <h5>Permissão: *</h5>
                </section>
                <Radio
                  name="role"
                  options={
                    initialData.role === Role.ADMIN
                      ? Roles
                      : Roles.filter(
                          r => r.id !== Role.ADMIN && r.id !== Role.SYSTEM,
                        )
                  }
                  handleSelected={updateSetor}
                  disabled={loadingSubmit || initialData.status === 'inativo'}
                />

                {showSetorField && (
                  <>
                    <section>
                      <h5>Setor: *</h5>
                    </section>
                    <Radio
                      name="setor"
                      options={filteredSetores}
                      disabled={
                        loadingSubmit || initialData.status === 'inativo'
                      }
                    />
                    {/* <section>
                      <h5>Incluir como avaliador?</h5>
                    </section>
                    <Radio
                      name="avaliadorprod"
                      options={[
                        { id: 'nao', label: 'NÃO' },
                        { id: 'sim', label: 'SIM' },
                      ]}
                      disabled={
                        loadingSubmit || initialData.status === 'inativo'
                      }
                    /> */}
                    {/* <section>
                      <h5>Incluir como comunicador?</h5>
                    </section>
                    <Radio
                      name="comunicador"
                      options={[
                        { id: 'nao', label: 'NÃO' },
                        { id: 'sim', label: 'SIM' },
                      ]}
                      disabled={
                        loadingSubmit || initialData.status === 'inativo'
                      }
                    /> */}
                  </>
                )}
                <section>
                  <h5>Exibidoras: *</h5>
                </section>
                <Checkbox
                  name="emissora"
                  options={emissoras}
                  disabled={loadingSubmit || initialData.status === 'inativo'}
                />
              </FormContent>
              <div className="ButtonGroup">
                <Button
                  type="submit"
                  disabled={loadingSubmit || initialData.status === 'inativo'}
                  color="primary"
                >
                  {!loadingSubmit ? 'SALVAR' : <Spinner type="ThreeDots" />}
                </Button>
              </div>
            </Form>
          )}
        </Content>
      </Card>
    </Container>
  );
}
